import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Production,
  ProductionInput,
  Show,
  ShowInput,
  Dance,
  DanceInput,
  ShowLineup,
  CastAssignmentInput,
} from '@pirouette/shared';
import { productionInputSchema, showInputSchema, danceInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

// PRODUCTIONS --------------------------------------------------------------

export function useProductions() {
  return useQuery({
    queryKey: ['productions'],
    queryFn: async (): Promise<Production[]> => {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProduction(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['production', id],
    queryFn: async (): Promise<Production | null> => {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useCreateProduction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProductionInput): Promise<Production> => {
      const parsed = productionInputSchema.parse(input);
      const { data, error } = await supabase
        .from('productions')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productions'] });
    },
  });
}

// SHOWS --------------------------------------------------------------------

export function useShows(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['shows', productionId],
    queryFn: async (): Promise<Show[]> => {
      const { data, error } = await supabase
        .from('shows')
        .select('*')
        .eq('production_id', productionId!)
        .is('deleted_at', null)
        .order('performance_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateShow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ShowInput): Promise<Show> => {
      const parsed = showInputSchema.parse(input);
      const { data, error } = await supabase
        .from('shows')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (show) => {
      void qc.invalidateQueries({ queryKey: ['shows', show.production_id] });
    },
  });
}

// DANCES -------------------------------------------------------------------

export function useDances(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['dances', productionId],
    queryFn: async (): Promise<Dance[]> => {
      const { data, error } = await supabase
        .from('dances')
        .select('*')
        .eq('production_id', productionId!)
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateDance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DanceInput): Promise<Dance> => {
      const parsed = danceInputSchema.parse(input);
      const { data, error } = await supabase
        .from('dances')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['dances', d.production_id] });
    },
  });
}

// LINEUPS ------------------------------------------------------------------

export function useShowLineup(showId: string | undefined) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['lineup', showId],
    queryFn: async (): Promise<ShowLineup[]> => {
      const { data, error } = await supabase
        .from('show_lineups')
        .select('*')
        .eq('show_id', showId!)
        .order('sequence_index', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSetLineupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      show_id: string;
      ordering: { dance_id: string; sequence_index: number }[];
    }) => {
      // Single atomic RPC — upserts the order AND runs the cascade in one
      // transaction with an advisory lock on show_id. Eliminates the
      // upsert↔recompute race two concurrent admins could otherwise trigger.
      const { error } = await supabase.rpc('apply_lineup_order', {
        p_show_id: args.show_id,
        p_ordering: args.ordering,
      });
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['lineup', args.show_id] });
    },
    onError: (_, args) => {
      // Force a fresh fetch so any local optimistic-revert uses authoritative
      // server state rather than the stale cached snapshot.
      void qc.invalidateQueries({ queryKey: ['lineup', args.show_id] });
    },
  });
}

export function useAddDanceToLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { show_id: string; dance_id: string }) => {
      // Atomic append — advisory lock + max(seq)+1 + insert + cascade,
      // all in one transaction. Eliminates the TOCTOU two concurrent
      // "Add to show" clicks would otherwise produce.
      const { error } = await supabase.rpc('append_dance_to_lineup', {
        p_show_id: args.show_id,
        p_dance_id: args.dance_id,
      });
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['lineup', args.show_id] });
    },
  });
}

export function useRemoveDanceFromLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { show_id: string; dance_id: string }) => {
      const { error } = await supabase
        .from('show_lineups')
        .delete()
        .eq('show_id', args.show_id)
        .eq('dance_id', args.dance_id);
      if (error) throw error;
      await supabase.rpc('recompute_show_timeline', { p_show_id: args.show_id });
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['lineup', args.show_id] });
    },
  });
}

// CAST ASSIGNMENTS ---------------------------------------------------------

export function useDanceCast(danceId: string | undefined) {
  return useQuery({
    enabled: !!danceId,
    queryKey: ['cast', danceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cast_assignments')
        .select('id, dance_id, dancer_id, role, dancers(id, first_name, last_name, preferred_name)')
        .eq('dance_id', danceId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAssignCast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CastAssignmentInput) => {
      const { error } = await supabase.from('cast_assignments').insert(stripUndefined(input));
      if (error) throw error;
    },
    onSuccess: (_, input) => {
      void qc.invalidateQueries({ queryKey: ['cast', input.dance_id] });
    },
  });
}

export function useRemoveCast() {
  const qc = useQueryClient();
  return useMutation({
    // Take the dance_id alongside the assignment id so we can scope the
    // invalidation. Previously this blew away every cached cast query
    // across all dances, triggering N refetches on a screen with N tabs.
    mutationFn: async (args: { id: string; dance_id: string }) => {
      const { error } = await supabase.from('cast_assignments').delete().eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['cast', args.dance_id] });
    },
  });
}

// SEAT MAP -----------------------------------------------------------------

export function useSeatMap(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['seat-map', productionId],
    queryFn: async () => {
      const { data: sections, error: e1 } = await supabase
        .from('seat_map_sections')
        .select('*')
        .eq('production_id', productionId!)
        .order('display_order');
      if (e1) throw e1;
      if (!sections || sections.length === 0) return { sections: [], seats: [] };
      const { data: seats, error: e2 } = await supabase
        .from('seats')
        .select('*')
        .in(
          'section_id',
          (sections as { id: string }[]).map((s) => s.id),
        );
      if (e2) throw e2;
      return { sections, seats: seats ?? [] };
    },
  });
}
